import { useCallback, useEffect, useMemo, useState } from "react";
import {
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

export type CannabinoidSectionId = "documents" | "marketplace";

const STORAGE_KEY = "cannabinoid-treatment-layout-v1";

const DEFAULT_SECTION_ORDER: CannabinoidSectionId[] = [
  "documents",
  "marketplace",
];

const DEFAULT_COLLAPSED: Record<CannabinoidSectionId, boolean> = {
  documents: false,
  marketplace: false,
};

interface LayoutStorage {
  sectionOrder: CannabinoidSectionId[];
  collapsedMap: Record<CannabinoidSectionId, boolean>;
}

function isSectionId(value: unknown): value is CannabinoidSectionId {
  return value === "documents" || value === "marketplace";
}

function loadFromStorage(): LayoutStorage {
  if (typeof window === "undefined") {
    return {
      sectionOrder: DEFAULT_SECTION_ORDER,
      collapsedMap: DEFAULT_COLLAPSED,
    };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {
        sectionOrder: DEFAULT_SECTION_ORDER,
        collapsedMap: DEFAULT_COLLAPSED,
      };
    }

    const parsed = JSON.parse(raw) as Partial<LayoutStorage>;
    const order = Array.isArray(parsed.sectionOrder)
      ? parsed.sectionOrder.filter(isSectionId)
      : DEFAULT_SECTION_ORDER;

    const mergedOrder =
      order.length === DEFAULT_SECTION_ORDER.length
        ? order
        : DEFAULT_SECTION_ORDER;

    return {
      sectionOrder: mergedOrder,
      collapsedMap: {
        documents: Boolean(parsed.collapsedMap?.documents),
        marketplace: Boolean(parsed.collapsedMap?.marketplace),
      },
    };
  } catch {
    return {
      sectionOrder: DEFAULT_SECTION_ORDER,
      collapsedMap: DEFAULT_COLLAPSED,
    };
  }
}

/** Ordem e estado colapsado das seções — padrão FluxZap / PatientDash. */
export function useCannabinoidSectionsLayout() {
  const initial = useMemo(() => loadFromStorage(), []);
  const [sectionOrder, setSectionOrder] = useState(initial.sectionOrder);
  const [collapsedMap, setCollapsedMap] = useState(initial.collapsedMap);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setSectionOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as CannabinoidSectionId);
      const newIndex = prev.indexOf(over.id as CannabinoidSectionId);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const toggleCollapsed = useCallback((sectionId: CannabinoidSectionId) => {
    setCollapsedMap((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload: LayoutStorage = { sectionOrder, collapsedMap };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [sectionOrder, collapsedMap]);

  return {
    sectionOrder,
    collapsedMap,
    sensors,
    handleDragEnd,
    toggleCollapsed,
    sortingStrategy: verticalListSortingStrategy,
    collisionDetection: closestCenter,
  };
}
